// Game logic utilities for Ultimate Texas Hold'em

export const shuffleDeck = (deck) => {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const getCardValue = (card) => {
  const value = card.slice(0, -1);
  if (value === 'A') return 14;
  if (value === 'K') return 13;
  if (value === 'Q') return 12;
  if (value === 'J') return 11;
  return parseInt(value);
};

export const getCardSuit = (card) => {
  return card.slice(-1);
};

// Convert card value to display name
export const getValueName = (value, plural = false) => {
  const names = {
    14: plural ? 'Aces' : 'Ace',
    13: plural ? 'Kings' : 'King',
    12: plural ? 'Queens' : 'Queen',
    11: plural ? 'Jacks' : 'Jack',
    10: plural ? 'Tens' : 'Ten',
    9: plural ? 'Nines' : 'Nine',
    8: plural ? 'Eights' : 'Eight',
    7: plural ? 'Sevens' : 'Seven',
    6: plural ? 'Sixes' : 'Six',
    5: plural ? 'Fives' : 'Five',
    4: plural ? 'Fours' : 'Four',
    3: plural ? 'Threes' : 'Three',
    2: plural ? 'Twos' : 'Two',
  };
  return names[value] || value;
};

// Get suit symbol
export const getSuitSymbol = (suit) => {
  const symbols = { 'H': '♥', 'D': '♦', 'C': '♣', 'S': '♠' };
  return symbols[suit] || suit;
};

export const getCardDisplay = (card) => {
  const value = card.slice(0, -1);
  const suit = card.slice(-1);
  const suitSymbols = {
    'H': '♥',
    'D': '♦',
    'C': '♣',
    'S': '♠'
  };
  return { value, suit: suitSymbols[suit], isRed: suit === 'H' || suit === 'D' };
};

// Evaluate pre-flop hand (2 cards only)
export const evaluatePreflopHand = (cards) => {
  if (!cards || cards.length !== 2) return { rank: 1, name: 'High Card', detailedName: 'High Card', highCards: [] };
  
  const val1 = getCardValue(cards[0]);
  const val2 = getCardValue(cards[1]);
  
  if (val1 === val2) {
    const valueName = getValueName(val1, true);
    return { rank: 2, name: 'Pair', detailedName: `Pair of ${valueName}`, highCards: [val1] };
  }
  
  const highVal = Math.max(val1, val2);
  return { rank: 1, name: 'High Card', detailedName: `${getValueName(highVal)} High`, highCards: [highVal, Math.min(val1, val2)] };
};

export const evaluateHand = (cards) => {
  if (cards.length < 5) return { rank: 0, name: 'Invalid', detailedName: 'Invalid', highCards: [] };
  
  const values = cards.map(getCardValue).sort((a, b) => b - a);
  const suits = cards.map(getCardSuit);
  
  const valueCounts = {};
  values.forEach(v => valueCounts[v] = (valueCounts[v] || 0) + 1);
  
  // Sort by count first, then by value
  const sortedValues = Object.entries(valueCounts)
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return parseInt(b[0]) - parseInt(a[0]);
    })
    .map(([v]) => parseInt(v));
  
  const counts = Object.values(valueCounts).sort((a, b) => b - a);
  
  const isFlush = suits.every(s => s === suits[0]);
  const flushSuit = isFlush ? getSuitSymbol(suits[0]) : '';
  const uniqueValues = [...new Set(values)].sort((a, b) => b - a);
  
  // Check for straight (including A-2-3-4-5)
  let isStraight = false;
  let straightHighCard = 0;
  
  if (uniqueValues.length === 5) {
    if (uniqueValues[0] - uniqueValues[4] === 4) {
      isStraight = true;
      straightHighCard = uniqueValues[0];
    } else if (uniqueValues[0] === 14 && uniqueValues[1] === 5 && uniqueValues[4] === 2) {
      // Ace-low straight (A-2-3-4-5)
      isStraight = true;
      straightHighCard = 5; // 5-high straight
    }
  }
  
  if (isFlush && isStraight && uniqueValues[0] === 14 && uniqueValues[1] === 13) {
    return { rank: 10, name: 'Royal Flush', detailedName: `Royal Flush ${flushSuit}`, highCards: [14] };
  }
  if (isFlush && isStraight) {
    return { rank: 9, name: 'Straight Flush', detailedName: `Straight Flush ${flushSuit} (${getValueName(straightHighCard)} high)`, highCards: [straightHighCard] };
  }
  if (counts[0] === 4) {
    return { rank: 8, name: 'Four of a Kind', detailedName: `Four ${getValueName(sortedValues[0], true)}`, highCards: sortedValues };
  }
  if (counts[0] === 3 && counts[1] === 2) {
    return { rank: 7, name: 'Full House', detailedName: `Full House (${getValueName(sortedValues[0], true)} full of ${getValueName(sortedValues[1], true)})`, highCards: sortedValues };
  }
  if (isFlush) {
    return { rank: 6, name: 'Flush', detailedName: `Flush ${flushSuit} (${getValueName(values[0])} high)`, highCards: values };
  }
  if (isStraight) {
    return { rank: 5, name: 'Straight', detailedName: `Straight (${getValueName(straightHighCard)} high)`, highCards: [straightHighCard] };
  }
  if (counts[0] === 3) {
    return { rank: 4, name: 'Three of a Kind', detailedName: `Three ${getValueName(sortedValues[0], true)}`, highCards: sortedValues };
  }
  if (counts[0] === 2 && counts[1] === 2) {
    return { rank: 3, name: 'Two Pair', detailedName: `Two Pair (${getValueName(sortedValues[0], true)} and ${getValueName(sortedValues[1], true)})`, highCards: sortedValues };
  }
  if (counts[0] === 2) {
    return { rank: 2, name: 'Pair', detailedName: `Pair of ${getValueName(sortedValues[0], true)}`, highCards: sortedValues };
  }
  return { rank: 1, name: 'High Card', detailedName: `${getValueName(values[0])} High`, highCards: values };
};

export const getBestHand = (playerCards, communityCards) => {
  const allCards = [...playerCards, ...communityCards];
  if (allCards.length < 5) return { rank: 0, name: 'Invalid', highCards: [] };
  
  let bestHand = { rank: 0, name: 'Invalid', highCards: [] };
  
  // Generate all 5-card combinations
  const combinations = getCombinations(allCards, 5);
  
  for (const combo of combinations) {
    const hand = evaluateHand(combo);
    if (compareHands(hand, bestHand) > 0) {
      bestHand = hand;
    }
  }
  
  return bestHand;
};

const getCombinations = (arr, size) => {
  const result = [];
  
  const combine = (start, combo) => {
    if (combo.length === size) {
      result.push([...combo]);
      return;
    }
    for (let i = start; i < arr.length; i++) {
      combo.push(arr[i]);
      combine(i + 1, combo);
      combo.pop();
    }
  };
  
  combine(0, []);
  return result;
};

// Compare two hands: returns positive if hand1 > hand2, negative if hand1 < hand2, 0 if equal
export const compareHands = (hand1, hand2) => {
  if (hand1.rank !== hand2.rank) {
    return hand1.rank - hand2.rank;
  }
  
  // Same rank, compare high cards
  for (let i = 0; i < Math.min(hand1.highCards.length, hand2.highCards.length); i++) {
    if (hand1.highCards[i] !== hand2.highCards[i]) {
      return hand1.highCards[i] - hand2.highCards[i];
    }
  }
  
  return 0; // Exact tie
};

// Dealer qualifies with pair or better
export const dealerQualifies = (dealerHand, communityCards) => {
  const bestHand = getBestHand(dealerHand, communityCards);
  return bestHand.rank >= 2; // Pair or better
};

// Trips bonus payout - pays regardless of dealer hand
export const calculateTripsBonus = (playerHand, communityCards, tripsBet) => {
  if (tripsBet <= 0) return { payout: 0, multiplier: 0 };
  
  const bestHand = getBestHand(playerHand, communityCards);
  const payouts = {
    10: 50, // Royal Flush - 50 to 1
    9: 40,  // Straight Flush - 40 to 1
    8: 30,  // Four of a Kind - 30 to 1
    7: 8,   // Full House - 8 to 1
    6: 6,   // Flush - 6 to 1
    5: 5,   // Straight - 5 to 1
    4: 3,   // Three of a Kind - 3 to 1
  };
  
  if (payouts[bestHand.rank]) {
    return { 
      payout: tripsBet + (tripsBet * payouts[bestHand.rank]), 
      multiplier: payouts[bestHand.rank],
      win: true 
    };
  }
  return { payout: 0, multiplier: 0, win: false }; // Lose trips bet
};

// Blind payout - only pays on straight or better when player wins
export const calculateBlindPayout = (playerHand, communityCards, blindBet, playerWins) => {
  if (blindBet <= 0) return { payout: 0, multiplier: 0 };
  
  const bestHand = getBestHand(playerHand, communityCards);
  
  // Blind payout table (only if player wins)
  const payouts = {
    10: 500, // Royal Flush - 500 to 1
    9: 50,   // Straight Flush - 50 to 1
    8: 10,   // Four of a Kind - 10 to 1
    7: 3,    // Full House - 3 to 1
    6: 1.5,  // Flush - 3 to 2
    5: 1,    // Straight - 1 to 1
  };
  
  if (playerWins) {
    if (payouts[bestHand.rank]) {
      // Straight or better - pays according to table
      return { 
        payout: blindBet + (blindBet * payouts[bestHand.rank]), 
        multiplier: payouts[bestHand.rank],
        push: false 
      };
    } else {
      // Less than straight - push (return bet)
      return { payout: blindBet, multiplier: 0, push: true };
    }
  }
  
  // Player loses - lose blind bet
  return { payout: 0, multiplier: 0, push: false };
};

// Calculate complete hand results
export const calculateHandResults = (playerCards, dealerCards, communityCards, anteBet, blindBet, playBet, tripsBet) => {
  const playerBest = getBestHand(playerCards, communityCards);
  const dealerBest = getBestHand(dealerCards, communityCards);
  const dealerQualified = dealerQualifies(dealerCards, communityCards);
  const comparison = compareHands(playerBest, dealerBest);
  
  let results = {
    playerHand: playerBest,
    dealerHand: dealerBest,
    dealerQualified,
    ante: { bet: anteBet, payout: 0, result: '' },
    blind: { bet: blindBet, payout: 0, result: '', multiplier: 0 },
    play: { bet: playBet, payout: 0, result: '' },
    trips: { bet: tripsBet, payout: 0, result: '', multiplier: 0 },
    totalBet: anteBet + blindBet + playBet + tripsBet,
    totalPayout: 0,
    netWin: 0,
    outcome: '' // 'win', 'lose', 'push'
  };
  
  // Trips bonus - independent of main hand result
  if (tripsBet > 0) {
    const tripsResult = calculateTripsBonus(playerCards, communityCards, tripsBet);
    results.trips.payout = tripsResult.payout;
    results.trips.multiplier = tripsResult.multiplier;
    results.trips.result = tripsResult.win ? `WIN (${tripsResult.multiplier}:1)` : 'LOSE';
  }
  
  if (comparison > 0) {
    // Player wins
    results.outcome = 'win';
    
    // Play bet - always wins 1:1
    results.play.payout = playBet * 2;
    results.play.result = 'WIN (1:1)';
    
    if (dealerQualified) {
      // Ante wins 1:1
      results.ante.payout = anteBet * 2;
      results.ante.result = 'WIN (1:1)';
    } else {
      // Ante pushes
      results.ante.payout = anteBet;
      results.ante.result = 'PUSH';
    }
    
    // Blind payout
    const blindResult = calculateBlindPayout(playerCards, communityCards, blindBet, true);
    results.blind.payout = blindResult.payout;
    results.blind.multiplier = blindResult.multiplier;
    results.blind.result = blindResult.push ? 'PUSH' : `WIN (${blindResult.multiplier}:1)`;
    
  } else if (comparison < 0) {
    // Dealer wins
    results.outcome = 'lose';
    results.ante.payout = 0;
    results.ante.result = 'LOSE';
    results.blind.payout = 0;
    results.blind.result = 'LOSE';
    results.play.payout = 0;
    results.play.result = 'LOSE';
    
  } else {
    // Push (tie)
    results.outcome = 'push';
    results.ante.payout = anteBet;
    results.ante.result = 'PUSH';
    results.blind.payout = blindBet;
    results.blind.result = 'PUSH';
    results.play.payout = playBet;
    results.play.result = 'PUSH';
  }
  
  results.totalPayout = results.ante.payout + results.blind.payout + results.play.payout + results.trips.payout;
  results.netWin = results.totalPayout - results.totalBet;
  
  return results;
};
